import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { BODY_CONTENT_GENERATE } from './components';
import useQueryParams from 'hooks/useQueryParams';
import { DataRoomNdaSigner } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import DataRoomNdaTableProviderContext from '../../providers/DataRoomNdaTableProvider/DataRoomNdaTableProvider.context';
import {
  FIXED_TABLE_COLUMNS,
  FIXED_COLUMN_TRANSLATIONS,
  FIXED_COLUMN_SIZE,
  DYNAMIC_TABLE_COLUMNS,
  DYNAMIC_COLUMN_SIZE,
} from './components/data';
import { getIsDesc, getSortBy, getSortValue } from 'utils';

import './DataRoomNdasTable.styles.scss';
import './DataRoomNdasTable.styles.responsive.scss';

const getIsNdaCell = (column: string) =>
  !Object.values(FIXED_TABLE_COLUMNS).includes(column);

type DataRoomNdasTableProps = {
  className?: string;
  isLoading: boolean;
  totalPages: number;
  items: DataRoomNdaSigner[];
};

const DataRoomNdasTable: React.FC<DataRoomNdasTableProps> = (props) => {
  const { className, isLoading, totalPages, items } = props;

  const { dataRoomNdas } = useContext(DataRoomNdaContext);
  const { openNdaDrawer, openNdaModal } = useContext(
    DataRoomNdaTableProviderContext,
  );

  const {
    params: { sort, dataRoomNdaId },
    setQueryParam,
  } = useQueryParams();

  const classes = classNames('data-room-ndas-table', className);

  const tableColumns = useMemo(
    () => [
      FIXED_TABLE_COLUMNS.NAME,
      FIXED_TABLE_COLUMNS.EMAIL,
      ...dataRoomNdas.map(({ _id }) => _id),
    ],
    [dataRoomNdas],
  );

  const sortingValues = useMemo(
    () =>
      dataRoomNdas.reduce(
        (acc, { _id }) => ({
          ...acc,
          [_id]: _id,
        }),
        {},
      ),
    [dataRoomNdas],
  );

  const columnsSize = useMemo(
    () => ({
      ...FIXED_COLUMN_SIZE,
      ...dataRoomNdas.reduce(
        (acc, { _id }) => ({
          ...acc,
          [_id]: DYNAMIC_COLUMN_SIZE,
        }),
        {},
      ),
    }),
    [dataRoomNdas],
  );

  const headerContent = useMemo(
    () => ({
      // checkbox: () => (
      //   <Checkbox checked={isAllSelected} onChange={onSelectAll} />
      // ),
      ...dataRoomNdas.reduce(
        (acc, { _id, name }) => ({
          ...acc,
          [_id]: <p className="nda-header">{name}</p>,
        }),
        {},
      ),
    }),
    [dataRoomNdas],
  );

  const renderedContent = useMemo(
    () =>
      tableColumns.reduce(
        (acc, column) => ({
          ...acc,
          [column]: (signer: DataRoomNdaSigner) => {
            const isNdaCell = getIsNdaCell(column);

            const colComponent = isNdaCell ? DYNAMIC_TABLE_COLUMNS.NDA : column;
            const dataRoomNdaId = isNdaCell ? column : undefined;

            return BODY_CONTENT_GENERATE[colComponent]({
              signer,
              dataRoomNdaId,
            });
          },
        }),
        {},
      ),
    [tableColumns],
  );

  const activeSort = useMemo(
    () => getSortValue(dataRoomNdaId, getIsDesc(sort)),
    [sort, dataRoomNdaId],
  );

  const updateSortValues = useCallback(
    (sort) => {
      const isDesc = getIsDesc(sort);
      const value = getSortBy(sort);

      setQueryParam('dataRoomNdaId', value);
      setQueryParam('sort', getSortValue('sortStatus', isDesc));
    },
    [setQueryParam],
  );

  const onSignerRowClick = useCallback(
    (signer: DataRoomNdaSigner) => {
      const { dataRoomNdaSignings } = signer;

      if (!dataRoomNdaSignings.length) {
        return;
      }

      if (dataRoomNdaSignings.length === 1) {
        openNdaDrawer(signer, dataRoomNdaSignings[0]);

        return;
      }

      openNdaModal(signer);
    },
    [openNdaDrawer, openNdaModal],
  );

  return (
    <div className={classes}>
      <ReorderResizeTable
        columns={tableColumns as Array<keyof DataRoomNdaSigner>}
        data={items}
        isDragDisabled
        isResizeDisabled
        totalPages={totalPages}
        isDataLoading={isLoading}
        sortingValues={sortingValues}
        customSizes={columnsSize}
        headingTranslations={FIXED_COLUMN_TRANSLATIONS}
        tableContentTransformations={renderedContent}
        tableHeaderContentTransformations={headerContent}
        sort={activeSort}
        updateSort={updateSortValues}
        onRowClick={onSignerRowClick}
      />
    </div>
  );
};

export default DataRoomNdasTable;
